/**
 * Plain words for every reason a hub receipt or bill is held.
 *
 * One sentence saying what happened, and one next step for each role. The
 * owner is never told to ask the owner. No code, id, UUID or snake_case
 * reaches a face; the code is added to the technical details by the presenter.
 *
 * The keys are the shared parser's (`ingest-worker/src/hold_reasons.ts`), so a
 * second page can reuse the same sentences without a second set of words.
 */

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import {
  holdReasonHubAccountName,
  type HoldReasonCode,
} from "../../../ingest-worker/src/hold_reasons.js";
import type { QueueItem } from "../types.js";
import { advice } from "./capabilities.js";
import { decimalToCents, formatCents } from "./money.js";
import { navLabel } from "./sentences.js";

export type HoldButton =
  | "pick_account"
  | "pick_location"
  | "book_suggested"
  | "confirm_account"
  | "book_pair"
  | "none";

export interface HoldWording {
  title: string;
  happened: string;
  ownerNext: string;
  amNext: string;
  button: HoldButton;
  blocked: boolean;
}

/**
 * The hold sentences `ui/card-presenter.ts` also shows on a card (rd r12
 * B1b-i). One literal each, here: the hold wording owns these words, and the
 * card presenter imports them rather than typing a second copy. The sentences
 * are unchanged — B1b-ii changes the rows the owner decided, once, on these
 * constants.
 */
export const HOLD_SENTENCE = {  monthClosed: "This month is closed, so this payment cannot be booked yet.",
  pickAccount: "Pick the account to book it to.",
  pickLocationAndAccount: "Pick the location and the account.",
  billLocationNotSetUp:
    "A property on this bill is not set up as a location in the books.",
  pickLocation: "Pick the location for this one.",
  pickLocationOrAddProperty: "Pick the location, or add the property in the books.",
  pickOneAccountAndLocation:
    "Pick one account and location for the whole payment.",
  billAccountNotListed:
    "An account on this bill is not in your hub account list yet.",
  pickAccountAndAddHubAccount:
    "Pick the account, and add the hub account to the list in the books.",
  billAccountIsOwnBank:
    "An account on this bill points at this company's own bank account.",
  pickRightAccount: "Pick the right account.",
  pickRightAccountAndFixHubList:
    "Pick the right account, and fix the hub account list in the books.",
  pickAccountOrWait: "Pick the account, or wait for the next update.",
  conflictClearsItself: "This clears itself once the conflict is gone.",
  waitForConflict: "Wait for the conflict to clear.",
  keepBankLine: "Keep the bank line if it is the right one.",
  keepingBankLineClears:
    "Keeping the bank line clears this on the next match pass.",
  doesNotClearAlone: "This does not clear on its own.",
  confirmAccount: "Confirm the account.",
  confirmOrPickAnother:
    "Confirm the match, pick another transaction, or reject it.",
} as const;

/**
 * Decision 2's sentence, composed after a real card action ("pick another
 * account"), so both roles read the decided words for an account nobody can
 * open in the console. The action itself is still on the card.
 */
const ACCOUNT_NOT_OPEN_NEXT = `Pick another account. ${advice(
  "open_account",
  "account_manager",
)}`;

/** A capability's advice as the second half of a sentence. */
function orAdvice(action: Parameters<typeof advice>[0]): string {
  const text = advice(action, "account_manager");
  return `${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

export const HOLD_WORDING: Readonly<Record<HoldReasonCode, HoldWording>> = {
  receipt_gl_account_unmapped: {
    title: "Hub account not in your list",
    happened: "The hub sent an account that is not in your account list yet.",
    ownerNext:
      "Pick the account, and add it to the hub account list in the books.",
    amNext: HOLD_SENTENCE.pickAccount,
    button: "pick_account",
    blocked: false,
  },
  receipt_review_flag: {
    title: "The hub flagged this receipt",
    happened: "The person who sent this receipt asked for a second look.",
    ownerNext: "Check it, then book it or pick another account.",
    amNext: "Check it, then book it or pick another account.",
    button: "book_suggested",
    blocked: false,
  },
  receipt_location_unconfigured: {
    title: "Pick the location",
    happened: "The receipt's property is not set up as a location in the books.",
    ownerNext: HOLD_SENTENCE.pickLocationOrAddProperty,
    amNext: HOLD_SENTENCE.pickLocation,
    button: "pick_location",
    blocked: false,
  },
  receipt_account_not_open: {
    title: "That account is not open yet",
    happened: "The account this receipt maps to is not open in the books yet.",
    ownerNext: ACCOUNT_NOT_OPEN_NEXT,
    amNext: ACCOUNT_NOT_OPEN_NEXT,
    button: "pick_account",
    blocked: false,
  },
  receipt_account_is_bank: {
    title: "This maps to your own bank account",
    happened:
      "The hub account on this receipt points at this company's own bank or card account.",
    ownerNext:
      HOLD_SENTENCE.pickRightAccountAndFixHubList,
    amNext: "Pick the right account to book it to.",
    button: "pick_account",
    blocked: false,
  },
  receipt_amount_date_disagrees: {
    title: "Receipt and bank line differ",
    happened: "The receipt's amount or date does not match this bank line.",
    ownerNext: "If the bank line is right, book it; otherwise pick another account.",
    amNext: "If the bank line is right, book it; otherwise pick another account.",
    button: "book_suggested",
    blocked: false,
  },
  receipt_before_books_start: {
    title: "This is before your books start",
    happened:
      "This bank line is dated before your books start, so it did not book itself.",
    ownerNext: "Book it only if it is not already in your opening balances.",
    amNext: "Book it only if it is not already in your opening balances.",
    button: "book_suggested",
    blocked: false,
  },
  receipt_evidence_refused: {
    title: "This needs a person to confirm",
    happened:
      "A receipt cannot show whether this is an owner, loan or company-to-company payment.",
    ownerNext: "Confirm the account if you know what this was.",
    amNext: HOLD_SENTENCE.confirmAccount,
    button: "confirm_account",
    blocked: false,
  },
  receipt_attachment_unreadable: {
    title: "The receipt could not be read",
    happened:
      "Books could not read the receipt details for this bank line, so it did not book itself.",
    ownerNext: HOLD_SENTENCE.pickAccount,
    amNext: HOLD_SENTENCE.pickAccount,
    button: "pick_account",
    blocked: false,
  },
  receipt_category_unreadable: {
    title: "The receipt could not be read",
    happened: "The receipt's category could not be read, so it did not book itself.",
    ownerNext: HOLD_SENTENCE.pickAccount,
    amNext: HOLD_SENTENCE.pickAccount,
    button: "pick_account",
    blocked: false,
  },
  receipt_lookup_failed: {
    title: "The receipt could not be looked up",
    happened: "Books could not look up the receipt for this bank line.",
    ownerNext: HOLD_SENTENCE.pickAccountOrWait,
    amNext: HOLD_SENTENCE.pickAccountOrWait,
    button: "pick_account",
    blocked: false,
  },
  receipt_card_not_attached: {
    title: "Book this between companies?",
    happened:
      "This was paid with a shared card that is not registered to the receipt's company.",
    ownerNext: `Book the pair, or register the card under ${navLabel("cards")}.`,
    amNext: `Book the pair, or ${orAdvice("register_shared_card")}`,
    button: "book_pair",
    blocked: false,
  },
  receipt_shared_card_outside_set: {
    title: "The card belongs to another company",
    happened: "The card on this receipt belongs to a company outside your group.",
    ownerNext: `Check the card under ${navLabel("cards")}, then pick the account.`,
    amNext: `Pick the account, or ${orAdvice("view_shared_cards")}`,
    button: "pick_account",
    blocked: false,
  },
  receipt_needed_for_vendor: {
    title: "This payee needs a receipt",
    happened:
      "Books knows this payee, but it books only with a receipt, and none has come in.",
    ownerNext: "Pick the account, or wait for the hub to send the receipt.",
    amNext: "Pick the account, or wait for the hub to send the receipt.",
    button: "pick_account",
    blocked: false,
  },
  receipt_not_confirmed: {
    title: "Receipt not confirmed",
    happened:
      "A receipt from the hub matched this, but the hub did not confirm the match.",
    ownerNext: "Check the receipt, then pick the account.",
    amNext: "Check the receipt, then pick the account.",
    button: "pick_account",
    blocked: false,
  },
  receipt_did_not_book: {
    title: "The receipt did not book itself",
    happened:
      "A receipt from the hub matched this, but its card or category is not set up to book it.",
    ownerNext:
      "Pick the account, and check the card and category in the books.",
    amNext: HOLD_SENTENCE.pickAccount,
    button: "pick_account",
    blocked: false,
  },
  receipt_match_conflict: {
    title: "Two receipts want the same bank line",
    happened: HOLD_SENTENCE.conflictClearsItself,
    ownerNext: HOLD_SENTENCE.waitForConflict,
    amNext: HOLD_SENTENCE.waitForConflict,
    button: "none",
    blocked: false,
  },
  receipt_match_refused: {
    title: "A receipt match was refused",
    happened: HOLD_SENTENCE.keepBankLine,
    ownerNext: HOLD_SENTENCE.keepingBankLineClears,
    amNext: HOLD_SENTENCE.keepingBankLineClears,
    button: "none",
    blocked: false,
  },
  receipt_match_review: {
    title: "Confirm this receipt match",
    happened: HOLD_SENTENCE.doesNotClearAlone,
    ownerNext: HOLD_SENTENCE.confirmOrPickAnother,
    amNext: HOLD_SENTENCE.confirmOrPickAnother,
    button: "none",
    blocked: false,
  },
  bill_amount_disagrees: {
    title: "Bill and bank line differ",
    happened: "The bill from the hub does not add up to this bank line.",
    ownerNext:
      "Pick the account to book it, or ask the hub to correct the bill.",
    amNext: "Pick the account to book it, or ask the hub to correct the bill.",
    button: "pick_account",
    blocked: false,
  },
  bill_line_unlocated: {
    title: "A bill line has no property",
    happened:
      "A line on this bill has no property, so it did not book itself.",
    ownerNext: HOLD_SENTENCE.pickLocationAndAccount,
    amNext: HOLD_SENTENCE.pickLocationAndAccount,
    button: "pick_location",
    blocked: false,
  },
  bill_location_not_in_ledger: {
    title: "Pick the location",
    happened:
      HOLD_SENTENCE.billLocationNotSetUp,
    ownerNext: HOLD_SENTENCE.pickLocationOrAddProperty,
    amNext: HOLD_SENTENCE.pickLocation,
    button: "pick_location",
    blocked: false,
  },
  bill_multiple_locations: {
    title: "This bill covers several properties",
    happened:
      "This bill covers several properties that do not share one location, so it did not book itself.",
    ownerNext: HOLD_SENTENCE.pickOneAccountAndLocation,
    amNext: HOLD_SENTENCE.pickOneAccountAndLocation,
    button: "pick_account",
    blocked: false,
  },
  bill_unmapped_gl_account: {
    title: "Hub account not in your list",
    happened: HOLD_SENTENCE.billAccountNotListed,
    ownerNext:
      HOLD_SENTENCE.pickAccountAndAddHubAccount,
    amNext: HOLD_SENTENCE.pickAccount,
    button: "pick_account",
    blocked: false,
  },
  bill_account_not_in_chart: {
    title: "That account is not open yet",
    happened: "An account this bill maps to is not open in the books yet.",
    ownerNext: ACCOUNT_NOT_OPEN_NEXT,
    amNext: ACCOUNT_NOT_OPEN_NEXT,
    button: "pick_account",
    blocked: false,
  },
  bill_line_maps_to_bank: {
    title: "This maps to your own bank account",
    happened:
      HOLD_SENTENCE.billAccountIsOwnBank,
    ownerNext:
      HOLD_SENTENCE.pickRightAccountAndFixHubList,
    amNext: HOLD_SENTENCE.pickRightAccount,
    button: "pick_account",
    blocked: false,
  },
  bill_judgment_account: {
    title: "This needs a person to confirm",
    happened:
      "This bill maps to an owner, loan or company-to-company account, which needs a person to confirm.",
    ownerNext: "Confirm the account if you know what this was.",
    amNext: HOLD_SENTENCE.confirmAccount,
    button: "confirm_account",
    blocked: false,
  },
  bill_period_locked: {
    title: "This bill is in a closed month",
    happened: HOLD_SENTENCE.monthClosed,
    ownerNext: advice("reopen_month", "owner"),
    amNext: advice("reopen_month", "account_manager"),
    button: "none",
    blocked: true,
  },
  bill_match_ambiguous: {
    title: "More than one bill fits",
    happened:
      "More than one bill from the hub could be this bank line, so none was used.",
    ownerNext:
      "Pick the account, or ask the hub to remove the duplicate bill.",
    amNext: "Pick the account, or ask the hub to remove the duplicate bill.",
    button: "pick_account",
    blocked: false,
  },
  bill_lookup_failed: {
    title: "The bill could not be read",
    happened: "Books could not read the bill for this bank line.",
    ownerNext: HOLD_SENTENCE.pickAccountOrWait,
    amNext: HOLD_SENTENCE.pickAccountOrWait,
    button: "pick_account",
    blocked: false,
  },
};

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function shortDate(iso: string | undefined): string | null {
  if (iso === undefined || iso.startsWith("1970-01-01")) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (match === null) return null;
  const month = Number(match[2]);
  const day = Number(match[3]);
  const label = SHORT_MONTHS[month - 1];
  if (label === undefined || !Number.isInteger(day) || day < 1 || day > 31) {
    return null;
  }
  return `${label} ${String(day)}`;
}

/** 40 characters at a word boundary; never a mid-word cut. */
function clipName(name: string): string {
  if (name.length <= 40) return name;
  const head = name.slice(0, 40);
  const lastSpace = head.lastIndexOf(" ");
  const cut = lastSpace > 0 ? head.slice(0, lastSpace) : head;
  return cut.trim();
}

function absCents(cents: bigint): bigint {
  return cents < 0n ? -cents : cents;
}

function reviewFlagSentence(item: QueueItem): string {
  switch (item.receiptContext?.reviewFlag) {
    case "wrong_card":
      return "The person who sent this receipt said it may be on the wrong card.";
    case "reimbursement":
      return "The person who sent this receipt said it was paid personally and needs paying back.";
    default:
      return "The person who sent this receipt asked for a second look.";
  }
}

/** What happened, with the hub facts a sentence can name filled in. */
export function holdHappened(code: HoldReasonCode, item: QueueItem): string {
  if (code === "receipt_gl_account_unmapped") {
    const raw = holdReasonHubAccountName(item.aiRationale);
    if (raw === null) return HOLD_WORDING[code].happened;
    const safe = amVisibleDisplayText(raw).replace(/\s+/g, " ").trim();
    const name = clipName(safe);
    if (name.length === 0) return HOLD_WORDING[code].happened;
    return `The hub sent the account "${name}", which is not in your account list yet.`;
  }
  if (code === "receipt_review_flag") {
    return reviewFlagSentence(item);
  }
  if (code === "receipt_amount_date_disagrees") {
    const context = item.receiptContext;
    if (context === undefined) return HOLD_WORDING[code].happened;
    const receiptCents = decimalToCents(context.amountDecimal);
    const bankCents =
      item.amount === undefined ? null : decimalToCents(item.amount);
    const receiptDate = shortDate(context.receiptDate);
    const bankDate = shortDate(item.txnDate);
    if (
      receiptCents === null ||
      bankCents === null ||
      receiptDate === null ||
      bankDate === null
    ) {
      return HOLD_WORDING[code].happened;
    }
    return (
      `The receipt says ${formatCents(absCents(receiptCents))} on ${receiptDate}, ` +
      `but the bank line is ${formatCents(absCents(bankCents))} on ${bankDate}.`
    );
  }
  return HOLD_WORDING[code].happened;
}
